import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";

import type { InferTransformedProcedureOutput } from "@goose/api";
import { UserId } from "@goose/schema/Salesforce";

import { Breadcrumbs } from "~/components/admin/Breadcrumbs";
import { DetailsTable } from "~/components/admin/DetailsTable";

import {
  ContactIcon,
  EvaluationIcon,
  OpportunityIcon,
  RequirementSetIcon,
  SalesforceUserIcon,
  UsersIcon,
} from "~/components/admin/Icons";

import { LinkedUsersTable } from "~/components/admin/LinkedUsersTable";
import { PageHeader } from "~/components/admin/PageHeader";
import { StatCard } from "~/components/admin/StatCard";
import { QueryState } from "~/components/QueryState";
import { TRPCClientErrorDisplay } from "~/components/TRPCClientErrorDisplay";
import { Button } from "~/components/ui/Button";
import { Spinner } from "~/components/ui/Spinner";
import { useTRPC } from "~/lib/trpc";

export const Route = createFileRoute(
  "/_authenticated/admin/organizations/$organizationId/users/$userId",
)({
  component: SalesforceUserDetailPage,
  params: {
    parse: ({ userId }) => {
      return {
        userId: UserId.parse(userId),
      };
    },
  },
});

type SalesforceUser = InferTransformedProcedureOutput<"salesforce.users.get">;

function UserStats({ user }: { user: SalesforceUser }) {
  const trpc = useTRPC();

  const requirementSetsCount = useQuery(
    trpc.opportunities.requirements.count.queryOptions({
      organizationId: user.organization.id,
      userId: user.id,
    }),
  );

  const evaluationsCount = useQuery(
    trpc.contacts.evaluations.count.queryOptions({
      organizationId: user.organization.id,
      userId: user.id,
    }),
  );

  const opportunityCount = useQuery(
    trpc.opportunities.count.queryOptions({
      organizationId: user.organization.id,
      userId: user.id,
    }),
  );

  const contactCount = useQuery(
    trpc.contacts.count.queryOptions({
      organizationId: user.organization.id,
      userId: user.id,
    }),
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <StatCard
        label="Opportunities"
        value={opportunityCount.data ?? 0}
        isLoading={opportunityCount.isLoading}
        error={opportunityCount.error}
        icon={<OpportunityIcon className="h-7 w-7" />}
      />
      <StatCard
        label="Req Sets"
        value={requirementSetsCount.data ?? 0}
        icon={<RequirementSetIcon className="h-7 w-7" />}
        isLoading={requirementSetsCount.isLoading}
        error={requirementSetsCount.error}
      />
      <StatCard
        label="Contacts"
        value={contactCount.data ?? 0}
        isLoading={contactCount.isLoading}
        error={contactCount.error}
        icon={<ContactIcon className="h-7 w-7" />}
      />
      <StatCard
        label="Evaluations"
        value={evaluationsCount.data ?? 0}
        isLoading={evaluationsCount.isLoading}
        error={evaluationsCount.error}
        icon={<EvaluationIcon className="h-7 w-7" />}
      />
      <StatCard
        label="Linked Users"
        value={user.userSalesforceUsers.length}
        icon={<UsersIcon className="h-7 w-7" />}
      />
    </div>
  );
}

function SalesforceUserDetailPage() {
  const trpc = useTRPC();
  const { organizationId, userId } = Route.useParams();

  const query = useQuery(
    trpc.salesforce.users.get.queryOptions({
      organizationId,
      userId,
    }),
  );

  return (
    <QueryState
      result={query}
      loading={
        <div className="flex justify-center items-center py-24">
          <Spinner size="lg" />
        </div>
      }
      error={(error) => <TRPCClientErrorDisplay error={error} />}
    >
      {(user) => (
        <div className="space-y-6">
          <PageHeader
            breadcrumbs={
              <Breadcrumbs>
                <Link to="/admin">Admin</Link>
                <Link to="/admin/organizations">Organizations</Link>
                <Link
                  to="/admin/organizations/$organizationId"
                  params={{ organizationId }}
                >
                  {user.organization.name}
                </Link>
                <span>{user.name ?? user.email}</span>
              </Breadcrumbs>
            }
            icon={SalesforceUserIcon}
            title={user.name ?? user.email}
            actions={
              <>
                <Button
                  as="a"
                  href={`${user.organization.url}/${user.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outline"
                  size="sm"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open in Salesforce
                </Button>
              </>
            }
          />

          <UserStats user={user} />

          <DetailsTable
            details={[
              {
                label: "ID",
                value: user.id,
              },
              {
                label: "Name",
                value: user.name ?? "—",
              },
              {
                label: "Email",
                value: user.email,
              },
              {
                label: "Username",
                value: user.username,
              },
              {
                label: "Organization",
                value: (
                  <Link
                    to="/admin/organizations/$organizationId"
                    params={{ organizationId }}
                    className="font-medium text-slate-900 underline transition-colors hover:text-slate-500"
                  >
                    {user.organization.name}
                  </Link>
                ),
              },
            ]}
          />

          <LinkedUsersTable users={user.userSalesforceUsers} />
        </div>
      )}
    </QueryState>
  );
}
